import useConditionalTimeout from 'beautiful-react-hooks/useConditionalTimeout';
import cx from 'classnames';
import PageActions from 'components/page-actions/PageActions';
import Typography from 'components/typography/Typography';
import { TYPOGRAPHY_VARIANTS } from 'components/typography/typography-utils';
import { I18nNamespace } from 'constants/i18n.const';
import {
  FRAME_TO_PAYMENT_DIRECTLY,
  PRINT_SHOW_BTN_NEXT_DURATION,
} from 'constants/photo.const';
import { emitSeqLog } from 'functions/seq-logging.func';
import { isEqualVal } from 'helpers/string.helper';
import { useNewPhotoLifeCycleStep } from 'hooks/useNewPhotoLifeCycleStep';
import { usePageTimer } from 'hooks/usePageTimer';
import { PaymentMethodEnum } from 'models/photo/transaction.model';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { usePrintPhotoApiActionMutation } from 'store/api/print.slice-api';
import { finishTestedBooth } from 'store/features/app/boothSlice';
import { useAppDispatch, useAppSelector } from 'store/store-hooks';
import './print.css';
import UserExperience from './UserExperience';

function Print() {
  const { t } = useTranslation([I18nNamespace.COMMON, I18nNamespace.PAGE]);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { getStartNodePath } = useNewPhotoLifeCycleStep();
  const [
    printPhotoApiAction,
    { isLoading: printApiLoading, isUninitialized: printApiUninitialized },
  ] = usePrintPhotoApiActionMutation();
  const [isShowButtonBack, setIsShowButtonBack] = useState(false);

  usePageTimer({
    forwardFunc: () => handleRedirectToStart(),
    isNotShowConfirm: true,
  });

  const {
    frameId,
    layoutId,
    transactionId,
    quantitySheet,
    paymentMethodForTest,
  } = useAppSelector((state) => state.selfeApp);
  const {
    boothInfo,
    isVerticalScreen,
    isOnFeedback,
    isShowQrTransactionImage,
  } = useAppSelector((state) => state.booth);

  // Redirect back lại bắt đầu
  const handleRedirectToStart = () => {
    emitSeqLog?.({ messageTemplate: `[Print] End transaction! <============` });
    navigate(getStartNodePath() || '');
  };

  // Lưu lại 1 lần Test Booth
  useEffect(() => {
    paymentMethodForTest === PaymentMethodEnum.TESTPC &&
      dispatch(finishTestedBooth());
  }, [dispatch, paymentMethodForTest]);

  useEffect(() => {
    if (!isShowQrTransactionImage) {
      const printNumber = isEqualVal(FRAME_TO_PAYMENT_DIRECTLY, frameId)
        ? Math.max(quantitySheet / 2, 1)
        : quantitySheet;
      printPhotoApiAction({
        transactionId,
        numberOfImage: printNumber,
        layoutId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show nút back sau khi chup ảnh thành công
  useConditionalTimeout(
    () => setIsShowButtonBack(true),
    PRINT_SHOW_BTN_NEXT_DURATION,
    !printApiLoading && !printApiUninitialized,
  );

  const printTitleI18nCode = useMemo(
    () =>
      boothInfo?.IsPrintingBooth
        ? 'pleaseWaitForPrint6X20'
        : 'pleaseWaitForPrint',
    [boothInfo?.IsPrintingBooth],
  );

  return (
    <>
      <div className="print-title-container">
        <div
          className={cx(
            'print-title-wrapper text-center whitespace-pre-line print-title-wrapper',
            {
              'is-has-feedback': isOnFeedback,
            },
          )}
        >
          {!isShowQrTransactionImage && (
            <Typography
              variant={TYPOGRAPHY_VARIANTS.H1}
              className="print-title"
              firstCapCase
            >
              {t(`${I18nNamespace.COMMON}:${printTitleI18nCode}`)}
            </Typography>
          )}
        </div>
        {isOnFeedback ? (
          <UserExperience />
        ) : (
          <>
            <PageActions
              hidePrev
              hideNext={!isShowButtonBack}
              NextButtonProps={{
                children: t('common:finish'),
              }}
              handleContinue={handleRedirectToStart}
              className={cx({
                'print__page-actions--vertical': isVerticalScreen,
              })}
            />
          </>
        )}
        <div className="print-page__mascot-placeholder" />
      </div>
    </>
  );
}

export default Print;
